import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import { v4 as uuidv4 } from 'uuid'
import { FilesFilterParams, FileType, FilesListResponse, CreateFileParams, UploadProgressCallback } from '@/types/file'
import { BulkDeleteResponse } from '@/types/general'
import { getErrorMessage } from '@/lib/utils'
import { SortEnum } from '@/enums'

// API functions will be defined in the api.ts file
// Importing them here for reference
import { fetchFiles, fetchFile, deleteFile, bulkDeleteFiles, createFile, updateFile, downloadFile } from '@/lib/api'

type FileStateType = {
  data: FileType | null
  isLoading: boolean
  error: string | null
}

type FilesListStateType = {
  data: FilesListResponse | null
  isLoading: boolean
  error: string | null
}

type FileDownloadItemType = {
  downloadId: string // Unique identifier for the download
  file: FileType
  progress: number
  status: 'pending' | 'downloading' | 'completed' | 'error' | 'canceled'
  error: string | null
  abortController: AbortController | null
}

type FileDownloadsStateType = {
  items: FileDownloadItemType[]
}

type FilesStoreState = {
  file: FileStateType
  filesList: FilesListStateType
  downloads: FileDownloadsStateType
  filters: FilesFilterParams
  selectedFileIds: string[]
  setFilters: (filters: Partial<FilesFilterParams>) => void
  resetFilters: () => void
  updateUrlParams: (searchParams: URLSearchParams) => void
  getUrlParams: () => URLSearchParams

  // API functions
  fetchFiles: (params?: FilesFilterParams) => Promise<FilesListResponse>
  resetFilesList: () => void

  // File operations
  getFile: (id: string) => Promise<FileType>
  createFile: (params: CreateFileParams) => Promise<FileType>
  updateFile: (id: string, formData: FormData, onProgress?: UploadProgressCallback) => Promise<FileType>
  deleteFile: (id: string) => Promise<void>
  bulkDeleteFiles: (ids: string[]) => Promise<BulkDeleteResponse>

  // Selection operations
  selectFile: (id: string) => void
  deselectFile: (id: string) => void
  toggleFileSelection: (id: string) => void
  selectAllFiles: (files: FileType[]) => void
  deselectAllFiles: () => void
  isFileSelected: (id: string) => boolean

  // Download operations
  downloadFile: (id: string) => Promise<Blob | undefined>
  addToDownloadQueue: (file: FileType) => void
  removeFromDownloadQueue: (id: string) => void
  cancelDownload: (id: string) => void
  clearDownloads: () => void
  resetFile: () => void
}

const defaultFilters: FilesFilterParams = {
  page: 1,
  size: 20,
  sort: SortEnum.ASC,
  sortBy: 'createdAt',
  q: '',
  drivers: undefined,
  accessLevels: undefined,
  onlyImages: undefined,
  createdAtStart: undefined,
  createdAtEnd: undefined,
  updatedAtStart: undefined,
  updatedAtEnd: undefined
}

const useFilesStore = create<FilesStoreState>()(
  devtools((set, get) => ({
    file: {
      data: null,
      isLoading: false,
      error: null
    },
    filesList: {
      data: null,
      isLoading: false,
      error: null
    },
    downloads: {
      items: []
    },
    selectedFileIds: [],
    filters: { ...defaultFilters },

    setFilters: newFilters => {
      set(state => {
        const updatedFilters = { ...state.filters }

        Object.entries(newFilters).forEach(([key, value]) => {
          if (value === undefined || value === null || value === '') {
            delete updatedFilters[key as keyof FilesFilterParams]
          } else {
            updatedFilters[key as keyof FilesFilterParams] = value as any
          }
        })

        return { filters: updatedFilters }
      })
    },

    resetFilters: () => set({ filters: { ...defaultFilters } }),

    updateUrlParams: searchParams => {
      const filters: FilesFilterParams = {}

      for (const [key, value] of searchParams.entries()) {
        if (!value) {
          continue
        }

        if (key === 'page' || key === 'size') {
          filters[key] = parseInt(value)
        } else if (key === 'sort') {
          filters[key] = value as SortEnum
        } else if (key === 'sortBy') {
          filters[key] = value as FilesFilterParams['sortBy']
        } else if (key === 'onlyImages') {
          filters[key] = value === 'true'
        } else if (key === 'drivers') {
          filters[key] = value.split(',') as FilesFilterParams['drivers']
        } else if (key === 'accessLevels') {
          filters[key] = value.split(',') as FilesFilterParams['accessLevels']
        } else {
          filters[key as keyof FilesFilterParams] = value as any
        }
      }

      set({ filters })
    },

    getUrlParams: () => {
      const params = new URLSearchParams()
      const { filters } = get()

      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          if ((key === 'drivers' || key === 'accessLevels') && Array.isArray(value)) {
            params.set(key, value.join(','))
          } else if (key === 'onlyImages') {
            params.set(key, String(value))
          } else {
            params.set(key, String(value))
          }
        }
      })

      return params
    },

    getFile: async id => {
      set(state => ({
        file: {
          ...state.file,
          isLoading: true,
          error: null
        }
      }))

      try {
        const data = await fetchFile(id)

        set(state => ({
          file: {
            ...state.file,
            data,
            isLoading: false
          }
        }))

        return data
      } catch (error) {
        const errorMessage = getErrorMessage(error)

        set(state => ({
          file: {
            ...state.file,
            error: errorMessage,
            isLoading: false
          }
        }))

        throw error
      }
    },

    createFile: async params => {
      set(state => ({
        file: {
          ...state.file,
          isLoading: true,
          error: null
        }
      }))

      try {
        const data = await createFile(params)

        set(state => ({
          file: {
            ...state.file,
            data,
            isLoading: false
          }
        }))

        return data
      } catch (error) {
        const errorMessage = getErrorMessage(error)

        set(state => ({
          file: {
            ...state.file,
            error: errorMessage,
            isLoading: false
          }
        }))

        throw error
      }
    },

    deleteFile: async id => {
      set(state => ({
        file: {
          ...state.file,
          isLoading: true,
          error: null
        }
      }))

      try {
        await deleteFile(id)

        set(state => ({
          file: {
            ...state.file,
            isLoading: false
          }
        }))
      } catch (error) {
        const errorMessage = getErrorMessage(error)

        set(state => ({
          file: {
            ...state.file,
            error: errorMessage,
            isLoading: false
          }
        }))

        throw error
      }
    },

    updateFile: async (id, formData, onProgress) => {
      set(state => ({
        file: {
          ...state.file,
          isLoading: true,
          error: null
        }
      }))

      try {
        const data = await updateFile(id, formData, onProgress)

        set(state => ({
          file: {
            ...state.file,
            data,
            isLoading: false
          }
        }))

        return data
      } catch (error) {
        const errorMessage = getErrorMessage(error)

        set(state => ({
          file: {
            ...state.file,
            error: errorMessage,
            isLoading: false
          }
        }))

        throw error
      }
    },

    resetFile: () => {
      set(state => ({
        file: {
          ...state.file,
          data: null,
          error: null
        }
      }))
    },

    // Selection operations
    selectFile: id => {
      set(state => {
        if (state.selectedFileIds.includes(id)) {
          return state
        }
        return { selectedFileIds: [...state.selectedFileIds, id] }
      })
    },

    deselectFile: id => {
      set(state => ({
        selectedFileIds: state.selectedFileIds.filter(fileId => fileId !== id)
      }))
    },

    toggleFileSelection: id => {
      set(state => {
        if (state.selectedFileIds.includes(id)) {
          return { selectedFileIds: state.selectedFileIds.filter(fileId => fileId !== id) }
        }
        return { selectedFileIds: [...state.selectedFileIds, id] }
      })
    },

    selectAllFiles: files => {
      set(() => ({
        selectedFileIds: files.map(file => file.id)
      }))
    },

    deselectAllFiles: () => {
      set(() => ({ selectedFileIds: [] }))
    },

    isFileSelected: id => {
      return get().selectedFileIds.includes(id)
    },

    // Bulk delete operation
    bulkDeleteFiles: async ids => {
      set(state => ({
        file: {
          ...state.file,
          isLoading: true,
          error: null
        }
      }))

      try {
        const response = await bulkDeleteFiles({ ids })

        set(state => ({
          file: {
            ...state.file,
            isLoading: false
          },
          // Only remove successfully deleted items from selection
          selectedFileIds: state.selectedFileIds.filter(id => !response.deletedItems.includes(id))
        }))

        return response
      } catch (error) {
        const errorMessage = getErrorMessage(error)

        set(state => ({
          file: {
            ...state.file,
            error: errorMessage,
            isLoading: false
          }
        }))

        throw error
      }
    },

    fetchFiles: async (params?: FilesFilterParams) => {
      const filtersToUse = params || get().filters

      set(state => ({
        filesList: {
          ...state.filesList,
          isLoading: true,
          error: null
        }
      }))

      try {
        const data = await fetchFiles(filtersToUse)

        set(state => ({
          filesList: {
            ...state.filesList,
            data,
            isLoading: false
          }
        }))

        return data
      } catch (error) {
        const errorMessage = getErrorMessage(error)

        set(state => ({
          filesList: {
            ...state.filesList,
            error: errorMessage,
            isLoading: false
          }
        }))

        throw error
      }
    },

    resetFilesList: () => {
      set(state => ({
        filesList: {
          ...state.filesList,
          data: null,
          error: null
        }
      }))
    },

    addToDownloadQueue: async file => {
      // Generate a unique download ID to allow multiple downloads of the same file
      const downloadId = `${file.id}_${uuidv4()}`

      // We don't need to check for existing items anymore since we're allowing multiple downloads
      // of the same file with different download IDs

      set(state => ({
        downloads: {
          items: [
            {
              downloadId,
              file,
              progress: 0,
              status: 'pending',
              error: null,
              abortController: null
            },
            ...state.downloads.items
          ]
        }
      }))

      await get().downloadFile(downloadId)
    },

    removeFromDownloadQueue: downloadId => {
      const { downloads } = get()
      const downloadItem = downloads.items.find(item => item.downloadId === downloadId)

      if (downloadItem && downloadItem.status === 'downloading' && downloadItem.abortController) {
        downloadItem.abortController.abort()
      }

      set(state => ({
        downloads: {
          items: state.downloads.items.filter(item => item.downloadId !== downloadId)
        }
      }))
    },

    cancelDownload: downloadId => {
      const { downloads } = get()
      const downloadItem = downloads.items.find(item => item.downloadId === downloadId)

      if (downloadItem && downloadItem.status === 'downloading' && downloadItem.abortController) {
        downloadItem.abortController.abort()
      }
    },

    clearDownloads: () => {
      set(state => ({
        downloads: {
          items: state.downloads.items.filter(item => item.status !== 'completed' && item.status !== 'canceled')
        }
      }))
    },

    downloadFile: async downloadId => {
      const abortController = new AbortController()
      const downloadItem = get().downloads.items.find(item => item.downloadId === downloadId)

      if (!downloadItem) {
        console.error(`Download item with ID ${downloadId} not found`)
        return
      }

      const id = downloadItem.file.id

      set(state => ({
        downloads: {
          items: state.downloads.items.map(item =>
            item.downloadId === downloadId
              ? {
                  ...item,
                  status: 'downloading',
                  progress: 0,
                  error: null,
                  abortController
                }
              : item
          )
        }
      }))

      try {
        const blob = await downloadFile(
          id,
          progress => {
            set(state => ({
              downloads: {
                items: state.downloads.items.map(item =>
                  item.downloadId === downloadId ? { ...item, progress } : item
                )
              }
            }))
          },
          abortController
        )

        set(state => ({
          downloads: {
            items: state.downloads.items.map(item =>
              item.downloadId === downloadId
                ? {
                    ...item,
                    status: 'completed',
                    progress: 100,
                    abortController: null
                  }
                : item
            )
          }
        }))

        const url = window.URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.style.display = 'none'
        a.href = url

        // Use the file name from the download item
        a.download = downloadItem.file.name || id

        document.body.appendChild(a)
        a.click()

        window.URL.revokeObjectURL(url)
        document.body.removeChild(a)

        return blob
      } catch (error) {
        const errorMessage = getErrorMessage(error)

        const isCancellationError =
          errorMessage.toLowerCase().includes('canceled') ||
          errorMessage.toLowerCase().includes('canceled') ||
          errorMessage.toLowerCase().includes('abort')

        set(state => ({
          downloads: {
            items: state.downloads.items.map(item =>
              item.downloadId === downloadId
                ? {
                    ...item,
                    status: isCancellationError ? 'canceled' : 'error',
                    error: isCancellationError ? null : errorMessage,
                    abortController: null
                  }
                : item
            )
          }
        }))

        if (!isCancellationError) {
          throw error
        }
      }
    }
  }))
)

export default useFilesStore
